
import React, { PureComponent } from 'react';

import { CHROME, EDGE, FIREFOX, SAFARI } from '../../../../browser';
import { Dialog } from '../../base/dialog';
import { translate } from '../../base/i18n';
import { Icon, IconWarning } from '../../base/icons';
import { browser } from '../../base/lib-meet-hour';
import { connect } from '../../base/redux';

const BowserDetector = require('@jitsi/js-utils').BrowserDetection;
const bdInstance = new BowserDetector();
const browserName = bdInstance.getName();

/**
 * Dialog that provides browser-specific guidance for resetting camera/microphone permissions.
 */
class PermissionGuidanceDialog extends PureComponent {
    /**
     * Renders browser-specific instructions.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderInstructions() {
        const isElectronApp = browser.isElectron();

        if (isElectronApp) {
            return this._renderElectronInstructions();
        }

        switch (browserName) {
        case CHROME:
            return this._renderChromeInstructions();
        case SAFARI:
            return this._renderSafariInstructions();
        case FIREFOX:
            return this._renderFirefoxInstructions();
        case EDGE:
            return this._renderEdgeInstructions();
        default:
            return this._renderGenericInstructions();
        }
    }

    /**
     * Renders Chrome-specific instructions.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderChromeInstructions() {
        const { t } = this.props;

        return (
            <div className = 'permission-guidance-content'>
                <div className = 'permission-guidance-icon IconWarningButton'>
                    <Icon
                        size = { 48 }
                        src = { IconWarning } />
                </div>
                <h3 className = 'permission-guidance-title'>
                    {t('deviceSelection.permissionDenied')}
                </h3>
                <p className = 'permission-guidance-description'>
                    {t('deviceSelection.permissionDeniedDescription')}
                </p>
                <div className = 'permission-guidance-steps'>
                    <ol className = 'permission-guidance-list'>
                        <li>
                            <strong>
                                {t('deviceSelection.step1Chrome')}
                            </strong>
                            {t('deviceSelection.step1ChromeDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step2Chrome')}
                            </strong>
                            {t('deviceSelection.step2ChromeDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step3')}
                            </strong>
                            {t('deviceSelection.step3Desc')}
                        </li>
                        <li>
                            {t('deviceSelection.resetPermissionsChrome')}
                        </li>
                    </ol>
                </div>
            </div>
        );
    }

    /**
     * Renders Safari-specific instructions.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderSafariInstructions() {
        const { t } = this.props;

        return (
            <div className = 'permission-guidance-content'>
                <div className = 'permission-guidance-icon IconWarningButton'>
                    <Icon
                        size = { 48 }
                        src = { IconWarning } />
                </div>
                <h3 className = 'permission-guidance-title'>
                    {t('deviceSelection.permissionDenied')}
                </h3>
                <p className = 'permission-guidance-description'>
                    {t('deviceSelection.permissionDeniedDescription')}
                </p>
                <div className = 'permission-guidance-steps'>
                    <ol className = 'permission-guidance-list'>
                        <li>
                            <strong>
                                {t('deviceSelection.step1Safari')}
                            </strong>
                            {t('deviceSelection.step1SafariDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step2Safari')}
                            </strong>
                            {t('deviceSelection.step2SafariDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step3Safari')}
                            </strong>
                            {t('deviceSelection.step3SafariDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step3')}
                            </strong>
                            {t('deviceSelection.step3Desc')}
                        </li>
                        <li>
                            {t('deviceSelection.resetPermissionsSafari')}
                        </li>
                    </ol>
                </div>
            </div>
        );
    }

    /**
     * Renders Firefox-specific instructions.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderFirefoxInstructions() {
        const { t } = this.props;

        return (
            <div className = 'permission-guidance-content'>
                <div className = 'permission-guidance-icon IconWarningButton'>
                    <Icon
                        size = { 48 }
                        src = { IconWarning } />
                </div>
                <h3 className = 'permission-guidance-title'>
                    {t('deviceSelection.permissionDenied')}
                </h3>
                <p className = 'permission-guidance-description'>
                    {t('deviceSelection.permissionDeniedDescription')}
                </p>
                <div className = 'permission-guidance-steps'>
                    <ol className = 'permission-guidance-list'>
                        <li>
                            <strong>
                                {t('deviceSelection.step1Firefox')}
                            </strong>
                            {t('deviceSelection.step1FirefoxDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step2Firefox')}
                            </strong>
                            {t('deviceSelection.step2FirefoxDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step3')}
                            </strong>
                            {t('deviceSelection.step3Desc')}
                        </li>
                        <li>
                            {t('deviceSelection.resetPermissionsFirefox')}
                        </li>
                    </ol>
                </div>
            </div>
        );
    }

    /**
     * Renders Edge-specific instructions.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderEdgeInstructions() {
        const { t } = this.props;

        return (
            <div className = 'permission-guidance-content'>
                <div className = 'permission-guidance-icon IconWarningButton'>
                    <Icon
                        size = { 48 }
                        src = { IconWarning } />
                </div>
                <h3 className = 'permission-guidance-title'>
                    {t('deviceSelection.permissionDenied')}
                </h3>
                <p className = 'permission-guidance-description'>
                    {t('deviceSelection.permissionDeniedDescription')}
                </p>
                <div className = 'permission-guidance-steps'>
                    <ol className = 'permission-guidance-list'>
                        <li>
                            <strong>
                                {t('deviceSelection.step1Edge')}
                            </strong>
                            {t('deviceSelection.step1EdgeDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step2Edge')}
                            </strong>
                            {t('deviceSelection.step2EdgeDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step3')}
                            </strong>
                            {t('deviceSelection.step3Desc')}
                        </li>
                        <li>
                            {t('deviceSelection.resetPermissionsEdge')}
                        </li>
                    </ol>
                </div>
            </div>
        );
    }

    /**
     * Renders Electron app instructions.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderElectronInstructions() {
        const { t } = this.props;

        return (
            <div className = 'permission-guidance-content'>
                <div className = 'permission-guidance-icon IconWarningButton'>
                    <Icon
                        size = { 48 }
                        src = { IconWarning } />
                </div>
                <h3 className = 'permission-guidance-title'>
                    {t('deviceSelection.permissionDenied')}
                </h3>
                <p className = 'permission-guidance-description'>
                    {t('deviceSelection.permissionDeniedElectronDesc')}
                </p>
                <div className = 'permission-guidance-steps'>
                    <ol className = 'permission-guidance-list'>
                        <li>
                            <strong>
                                {t('deviceSelection.step1Electron')}
                            </strong>
                            {t('deviceSelection.step1ElectronDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step2Electron')}
                            </strong>
                            {t('deviceSelection.step2ElectronDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step3Electron')}
                            </strong>
                            {t('deviceSelection.step3ElectronDesc')}
                        </li>
                        <li>
                            {t('deviceSelection.resetPermissionsElectron')}
                        </li>
                    </ol>
                </div>
            </div>
        );
    }

    /**
     * Renders generic browser instructions.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderGenericInstructions() {
        const { t } = this.props;

        return (
            <div className = 'permission-guidance-content'>
                <div className = 'permission-guidance-icon IconWarningButton'>
                    <Icon
                        size = { 48 }
                        src = { IconWarning } />
                </div>
                <h3 className = 'permission-guidance-title'>
                    {t('deviceSelection.permissionDenied')}
                </h3>
                <p className = 'permission-guidance-description'>
                    {t('deviceSelection.permissionDeniedDescription')}
                </p>
                <div className = 'permission-guidance-steps'>
                    <ol className = 'permission-guidance-list'>
                        <li>
                            <strong>
                                {t('deviceSelection.step1Generic')}
                            </strong>
                            {t('deviceSelection.step1GenericDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step2Generic')}
                            </strong>
                            {t('deviceSelection.step2GenericDesc')}
                        </li>
                        <li>
                            <strong>
                                {t('deviceSelection.step3')}
                            </strong>
                            {t('deviceSelection.step3Desc')}
                        </li>
                        <li>
                            {t('deviceSelection.resetPermissionsGeneric')}
                        </li>
                    </ol>
                </div>
            </div>
        );
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {

        return (
            <Dialog
                hideCancelButton = { true }
                okKey = 'dialog.close'
                titleKey = 'deviceSelection.permissionsRequired'
                width = 'medium'>
                {this._renderInstructions()}
            </Dialog>
        );
    }
}

export default translate(connect()(PermissionGuidanceDialog));
